import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { C } from '../lib/theme';
import { computeWeightProgress } from '../lib/weightRate';

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

function Chart({ data, color, height, showGrid }) {
  const weights = data.map(d => d.peso);
  const minY = +(Math.min(...weights) - 1).toFixed(0);
  const maxY = +(Math.max(...weights) + 1).toFixed(0);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 10, right: 16, left: -4, bottom: 0 }}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#E9F0EC" />}
        <XAxis
          dataKey="fecha"
          tick={{ fontSize: 11, fill: C.gray }}
          tickLine={false}
          axisLine={{ stroke: '#E5E7EB' }}
        />
        <YAxis
          domain={[minY, maxY]}
          tick={{ fontSize: 11, fill: C.gray }}
          tickLine={false}
          axisLine={false}
          tickFormatter={v => `${v} kg`}
          width={48}
        />
        <Tooltip
          formatter={v => [`${v} kg`, 'Peso']}
          labelFormatter={l => `Fecha: ${l}`}
          contentStyle={{
            borderRadius: 10,
            border: `1px solid ${C.mint}`,
            fontSize: '0.85rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        />
        <Line
          type="monotone"
          dataKey="peso"
          stroke={color}
          strokeWidth={2.5}
          dot={{ fill: color, r: 4, strokeWidth: 0 }}
          activeDot={{ r: 6, fill: color, stroke: '#fff', strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function WeightChart({ logs, targetDate, goalWeight }) {
  const [expanded, setExpanded] = useState(false);

  if (!logs || logs.length < 2) {
    return (
      <div style={{ textAlign: 'center', color: C.gray, padding: '2rem', fontSize: '0.9rem' }}>
        Registra al menos 2 pesos para ver la gráfica
      </div>
    );
  }

  const progress = computeWeightProgress(logs, targetDate, goalWeight);
  const data = logs.map(l => ({ fecha: formatDate(l.logged_at), peso: l.weight }));

  let color = C.gray;
  let label = 'Registrando tu evolución';
  if (progress.phase === 'trend_only') {
    color = C.sage;
    label = 'Tu evolución';
  } else if (progress.phase === 'on_pace_check') {
    color = progress.onPace ? C.sage : C.gray;
    label = progress.onPace ? 'En línea con tu objetivo' : 'Por debajo del ritmo hacia tu objetivo';
  }

  return (
    <>
      <Chart data={data} color={color} height={130} showGrid={false} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.4rem' }}>
        <span style={{ fontSize: '0.78rem', color, fontWeight: 700 }}>{label}</span>
        <button
          onClick={() => setExpanded(true)}
          style={{
            fontSize: '0.78rem', color: C.gray, background: 'none',
            border: `1px solid #E5E7EB`, borderRadius: 6,
            padding: '0.25rem 0.65rem', cursor: 'pointer',
          }}
        >
          Ampliar →
        </button>
      </div>

      {expanded && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: C.cream,
          display: 'flex', flexDirection: 'column',
          padding: '1.5rem 1rem 2rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
            <div>
              <h2 style={{ margin: '0 0 0.2rem', fontFamily: 'Georgia, serif', color: C.carbon, fontSize: '1.3rem' }}>
                Evolución del peso
              </h2>
              <span style={{ fontSize: '0.85rem', color, fontWeight: 700 }}>{label}</span>
            </div>
            <button
              onClick={() => setExpanded(false)}
              style={{
                background: 'none', border: `1.5px solid #E5E7EB`,
                borderRadius: 8, padding: '0.4rem 0.9rem',
                cursor: 'pointer', fontSize: '0.9rem', color: C.gray,
              }}
            >
              ✕ Cerrar
            </button>
          </div>

          <div style={{ flex: 1 }}>
            <Chart data={data} color={color} height={320} showGrid />
          </div>
        </div>
      )}
    </>
  );
}
